import { useEffect, useRef, useState } from 'react';
import { CircleHelpIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button.js';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from '@/components/ui/popover.js';
import {
  generationGuideLink,
  type GenerationGuideTopic,
} from './generation-guide-links.js';

export function GenerationHelp({
  label,
  children,
  text,
  guide,
}: {
  label: string;
  children?: React.ReactNode;
  text?: string;
  guide?: GenerationGuideTopic;
}) {
  const [open, setOpen] = useState(false);
  const pinned = useRef(false);
  const trigger = useRef<HTMLButtonElement>(null);
  const content = useRef<HTMLDivElement>(null);
  const guideAnchor = useRef<HTMLAnchorElement>(null);
  const restoringFocus = useRef(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const guideLink = guide ? generationGuideLink(guide) : null;
  function clearCloseTimer() {
    if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    closeTimer.current = null;
  }
  function closeHelp(restoreFocus = false) {
    clearCloseTimer();
    pinned.current = false;
    setOpen(false);
    if (restoreFocus) {
      restoringFocus.current = true;
      trigger.current?.focus();
      restoringFocus.current = false;
    }
  }
  function openHelp() {
    clearCloseTimer();
    setOpen(true);
  }
  function scheduleClose() {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      if (
        !pinned.current &&
        !trigger.current?.contains(document.activeElement) &&
        !content.current?.contains(document.activeElement)
      )
        closeHelp();
    }, 180);
  }
  useEffect(
    () => () => {
      if (closeTimer.current !== null) clearTimeout(closeTimer.current);
    },
    [],
  );
  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <Popover
        open={open}
        onOpenChange={(next) => (next ? openHelp() : closeHelp())}
      >
        <PopoverTrigger asChild>
          <Button
            ref={trigger}
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 shrink-0 !rounded-md p-0 text-muted-foreground"
            aria-label={label}
            onPointerEnter={(event) => {
              if (event.pointerType !== 'touch') openHelp();
            }}
            onPointerLeave={scheduleClose}
            onFocus={() => {
              if (!restoringFocus.current) openHelp();
            }}
            onBlur={scheduleClose}
            onClick={(event) => {
              event.preventDefault();
              if (pinned.current) closeHelp();
              else {
                pinned.current = true;
                openHelp();
              }
            }}
            onKeyDown={(event) => {
              if (event.key === 'Escape' && open) {
                event.preventDefault();
                event.stopPropagation();
                closeHelp(true);
              } else if (
                event.key === 'Tab' &&
                !event.shiftKey &&
                open &&
                guideAnchor.current
              ) {
                event.preventDefault();
                guideAnchor.current.focus();
              }
            }}
          >
            <CircleHelpIcon className="size-3.5" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          ref={content}
          aria-label={label}
          aria-labelledby={undefined}
          className="max-w-[calc(100vw-2rem)] space-y-3 p-3 text-xs"
          onOpenAutoFocus={(event) => event.preventDefault()}
          onCloseAutoFocus={(event) => event.preventDefault()}
          onPointerEnter={clearCloseTimer}
          onPointerLeave={scheduleClose}
          onFocusCapture={clearCloseTimer}
          onBlurCapture={scheduleClose}
          onEscapeKeyDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
            closeHelp(true);
          }}
        >
          <div>{children ?? text}</div>
          {guideLink && (
            <a
              ref={guideAnchor}
              href={guideLink.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-normal text-foreground underline underline-offset-4"
            >
              {guideLink.label}
              <ExternalLinkIcon className="size-3" aria-hidden="true" />
            </a>
          )}
        </PopoverContent>
      </Popover>
    </span>
  );
}

/** A quiet starting point for authors who are learning the generation workflow. */
export function GenerationGuideCard() {
  return (
    <div
      className="space-y-1.5 rounded-lg border bg-muted/20 p-3 text-xs"
      role="group"
      aria-label="Learn test generation"
    >
      <p className="font-medium">Learn test generation</p>
      <p className="text-muted-foreground">
        Choose useful context, shape the plan and review cases before saving.
      </p>
      <div className="flex flex-wrap gap-x-3 gap-y-1">
        <a
          href={generationGuideLink('plan').href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-foreground underline underline-offset-4"
        >
          Generation guide
        </a>
        <a
          href={generationGuideLink('context').href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-foreground underline underline-offset-4"
        >
          Prepare sources
        </a>
      </div>
    </div>
  );
}
